/* eufymake.js
 * Vanilla port of the interactive bits from the React prototype.
 * Wired up after DOMContentLoaded; everything is delegated by data-attribute
 * so the markup in templates stays declarative.
 */
(function () {
  'use strict';

  /* ── Scroll reveal: add .in to any .reveal element when it enters view ── */
  function revealAll(root) {
    (root || document).querySelectorAll('.reveal').forEach((el) => el.classList.add('in'));
  }
  function initReveal() {
    // In the theme editor (or without IntersectionObserver) reveal everything
    // immediately — otherwise a freshly re-rendered section stays at opacity:0
    // and looks empty while editing.
    if ((window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) {
      revealAll();
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
  }

  /* ── Header: add a subtle shadow after 8px of scroll ────────────────── */
  function initHeaderShadow() {
    const header = document.querySelector('.header');
    if (!header) return;
    const update = () => {
      header.style.boxShadow = window.scrollY > 8
        ? '0 6px 24px -16px rgba(10,10,11,0.2)'
        : 'none';
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ── FAQ accordion: only one item open at a time ────────────────────── */
  function initFAQ(scope) {
    const items = (scope || document).querySelectorAll('.faq__item');
    items.forEach((item) => {
      item.addEventListener('click', () => {
        const wasOpen = item.classList.contains('open');
        items.forEach((i) => i.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
      });
    });
    // Open the first item by default to match the prototype.
    if (items.length) items[0].classList.add('open');
  }

  /* ── Gallery filters: hide tiles whose data-mat doesn't match ───────── */
  function initGalleryFilters(scope) {
    const root = (scope || document).querySelector('[data-gallery]');
    if (!root) return;
    const buttons = root.querySelectorAll('.gallery__filters .mfilter');
    const tiles   = root.querySelectorAll('.gtile');
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const val = btn.dataset.filter;
        buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
        tiles.forEach((t) => {
          const show = val === 'all' || t.dataset.mat === val;
          t.style.display = show ? '' : 'none';
        });
      });
    });
  }

  /* ── Machine page: switch hero illustration when a tab is clicked ───── */
  function initMachineTabs(scope) {
    const root = (scope || document).querySelector('[data-machine-tabs]');
    if (!root) return;
    const tabs    = root.querySelectorAll('.tab');
    const visuals = root.querySelectorAll('[data-machine-variant]');
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.dataset.tab;
        tabs.forEach((t) => t.setAttribute('aria-pressed', String(t === tab)));
        visuals.forEach((v) => {
          v.style.display = v.dataset.machineVariant === id ? '' : 'none';
        });
      });
    });
  }

  /* ── ROI calculator ─────────────────────────────────────────────────── */
  function initROI(scope) {
    const root = (scope || document).querySelector('[data-roi]');
    if (!root) return;

    // Machine config is read from the buttons (set via the section settings),
    // so prices/names/labels are editable in the theme editor. Falls back to
    // the original defaults if the markup carries no data-* config.
    const INV = {}, NAME = {}, LABEL = {};
    root.querySelectorAll('[data-roi-machine]').forEach((btn) => {
      const key = btn.dataset.roiMachine;
      if (btn.dataset.roiInv !== undefined && btn.dataset.roiInv !== '') INV[key] = parseFloat(btn.dataset.roiInv);
      if (btn.dataset.roiName)  NAME[key]  = btn.dataset.roiName;
      if (btn.dataset.roiLabel) LABEL[key] = btn.dataset.roiLabel;
    });
    if (!Object.keys(INV).length) {
      Object.assign(INV,   { zz1s: 2900, zz2e: 3316.67, '6090': 9900 });
      Object.assign(NAME,  { zz1s: 'RF-ZZ1S', zz2e: 'RF-ZZ2E', '6090': 'RF-6090 PRO' });
      Object.assign(LABEL, { zz1s: 'RF-ZZ1S · 2 900 € HT', zz2e: 'RF-ZZ2E · 3 316 € HT', '6090': 'RF-6090 PRO · sur devis' });
    }

    const numAttr = (v, d) => { const n = parseFloat(v); return isNaN(n) ? d : n; };
    const state = {
      machine: root.dataset.defaultMachine || 'zz2e',
      pieces:  numAttr(root.dataset.pieces, 80),
      price:   numAttr(root.dataset.price, 28),
      cost:    numAttr(root.dataset.cost, 2.4),
    };
    if (!(state.machine in INV)) state.machine = Object.keys(INV)[0];

    const fr = (n) => n.toLocaleString('fr-FR');

    // Update every output span. All outputs are identified by data-roi-out.
    function render() {
      const revenue = state.pieces * state.price;
      const matCost = state.pieces * state.cost;
      const margin  = revenue - matCost;
      const months  = Math.max(1, Math.ceil(INV[state.machine] / margin));
      const yr      = margin * 12;
      const mPct    = revenue > 0 ? Math.round((margin / revenue) * 100) : 0;

      const set = (k, v) => {
        root.querySelectorAll('[data-roi-out="' + k + '"]').forEach((el) => {
          el.textContent = v;
        });
      };
      set('machine-label', LABEL[state.machine]);
      set('machine-name',  NAME[state.machine]);
      set('pieces',  state.pieces);
      set('price',   state.price);
      set('cost',    state.cost.toFixed(2));
      set('months',  months);
      set('revenue', fr(revenue) + ' €');
      set('margin',  fr(margin)  + ' €');
      set('mpct',    mPct + ' %');
      set('yearrev', fr(revenue * 12) + ' €');
      set('yearmar', fr(yr) + ' €');
    }

    root.querySelectorAll('[data-roi-machine]').forEach((btn) => {
      btn.addEventListener('click', () => {
        state.machine = btn.dataset.roiMachine;
        root.querySelectorAll('[data-roi-machine]').forEach((b) => {
          b.setAttribute('aria-pressed', String(b === btn));
        });
        render();
      });
    });

    const bind = (sel, key, parse) => {
      const el = root.querySelector(sel);
      if (!el) return;
      el.addEventListener('input', () => { state[key] = parse(el.value); render(); });
    };
    bind('[data-roi-input="pieces"]', 'pieces', (v) => +v);
    bind('[data-roi-input="price"]',  'price',  (v) => +v);
    bind('[data-roi-input="cost"]',   'cost',   (v) => +v);

    render();
  }

  /* ── Multi-step Devis form with live recommendation ─────────────────── */
  function initDevis(scope) {
    const root = (scope || document).querySelector('[data-devis]');
    if (!root) return;
    const TOTAL = 5;
    const form = {
      activity: '', materials: [], volume: '', format: '', budget: '',
      name: '', company: '', email: '', phone: '', when: '', note: '',
    };
    let step = 1;

    const REC = {
      '6090': {
        name: 'RF-6090 PRO', price: 'Sur devis',
        lease: 'Configuration à étudier', pill: 'PRO',
        variant: '6090',
        bullets: ['600 × 900 mm · grand format', '2× Epson XP600 · CMYKW + V', 'Hauteur 20 cm', 'Cadence production'],
      },
      zz1s: {
        name: 'RF-ZZ1S', price: 'à partir de 2 900 € HT',
        lease: 'Pack PREMIUM · 3 900 € HT', pill: 'A3',
        variant: 'a3',
        bullets: ['330 × 600 mm A3+', 'Tête Epson XP600 · CMYKW', 'Hauteur 10 cm', 'Idéal coques & goodies'],
      },
      zz2e: {
        name: 'RF-ZZ2E', price: 'à partir de 3 316,67 € HT',
        lease: 'Pack PREMIUM · 4 150 € HT', pill: 'Best-seller',
        variant: 'a2',
        bullets: ['320 × 520 mm A3+', 'XP600 Piezo · CMYKW + Vernis', 'Hauteur 20 cm', 'Effet relief 3D embossé'],
      },
    };

    function recommend() {
      const v = form.volume, f = form.format, b = form.budget;
      const mats = form.materials.length;
      if (v === 'prod' || v === 'high' || f === 'big' || b === 'big') return REC['6090'];
      if ((v === 'low' && b === 'small') || (mats <= 2 && b === 'small')) return REC.zz1s;
      return REC.zz2e;
    }

    // Step validation gates the "Next" button.
    function canNext() {
      if (step === 1) return !!form.activity;
      if (step === 2) return form.materials.length > 0;
      if (step === 3) return !!form.volume && !!form.format;
      if (step === 4) return !!form.budget;
      if (step === 5) {
        return form.name.trim() && form.email.trim() && form.phone.trim();
      }
      return false;
    }

    function render() {
      // Show only the current step.
      root.querySelectorAll('[data-step]').forEach((el) => {
        const n = +el.dataset.step;
        el.style.display = n === step ? '' : 'none';
      });

      // Progress bar.
      root.querySelectorAll('[data-progress]').forEach((el, i) => {
        const n = i + 1;
        el.className = n < step ? 'done' : n === step ? 'cur' : '';
      });

      // Header chip + back button + next button.
      const chip = root.querySelector('[data-step-chip]');
      if (chip) {
        const titles = ['Votre activité', 'Vos supports', 'Volume & format', 'Budget envisagé', 'Vos coordonnées'];
        chip.textContent = step > TOTAL
          ? 'Confirmation'
          : 'Étape ' + step + '/' + TOTAL + ' · ' + titles[step - 1];
      }

      const back = root.querySelector('[data-back]');
      if (back) {
        back.disabled = step === 1;
        back.style.opacity = step === 1 ? '0.4' : '1';
      }

      const next = root.querySelector('[data-next]');
      if (next) {
        const ok = canNext();
        next.disabled = !ok;
        next.style.opacity = ok ? '1' : '0.55';
        next.className = 'btn ' + (ok ? 'btn--accent' : 'btn--ghost') + ' btn--sm';
        const label = step === TOTAL ? 'Envoyer ma demande' : 'Étape suivante';
        next.querySelector('[data-next-label]').textContent = label;
      }

      // Footer actions hidden on the success screen.
      const actions = root.querySelector('.quote__actions');
      if (actions) actions.style.display = step > TOTAL ? 'none' : '';

      // Live recommendation panel.
      const rec = recommend();
      const recRoot = root.querySelector('[data-rec]');
      if (recRoot) {
        recRoot.querySelector('[data-rec-name]').textContent = rec.name;
        recRoot.querySelector('[data-rec-price]').textContent = rec.price;
        recRoot.querySelector('[data-rec-lease]').textContent = rec.lease;
        recRoot.querySelector('[data-rec-pill]').textContent = rec.pill;
        const ul = recRoot.querySelector('[data-rec-bullets]');
        ul.innerHTML = '';
        rec.bullets.forEach((b) => {
          const li = document.createElement('li');
          li.innerHTML = '<span style="color: var(--accent)">✓</span> ' + b;
          ul.appendChild(li);
        });
        recRoot.querySelectorAll('[data-rec-variant]').forEach((el) => {
          el.style.display = el.dataset.recVariant === rec.variant ? '' : 'none';
        });
      }
    }

    // Radio options (step 1, step 5 timing chips).
    root.querySelectorAll('[data-radio]').forEach((el) => {
      el.addEventListener('click', () => {
        const k = el.dataset.radio;
        const v = el.dataset.value;
        form[k] = v;
        root.querySelectorAll('[data-radio="' + k + '"]').forEach((sib) => {
          sib.classList.toggle('is-on', sib === el);
        });
        render();
      });
    });

    // Multi-select chips (step 2 materials).
    root.querySelectorAll('[data-toggle]').forEach((el) => {
      el.addEventListener('click', () => {
        const k = el.dataset.toggle;
        const v = el.dataset.value;
        const i = form[k].indexOf(v);
        if (i >= 0) form[k].splice(i, 1);
        else form[k].push(v);
        el.classList.toggle('is-on');
        render();
      });
    });

    // Text inputs / textarea (step 5).
    root.querySelectorAll('[data-field]').forEach((el) => {
      el.addEventListener('input', () => {
        form[el.dataset.field] = el.value;
        render();
      });
    });

    // Step navigation + real Shopify contact-form submission.
    const back = root.querySelector('[data-back]');
    const contactForm = root.querySelector('form');

    function syncHidden() {
      const setH = (k, v) => { const el = root.querySelector('[data-hidden="' + k + '"]'); if (el) el.value = v || ''; };
      setH('activity', form.activity);
      setH('materials', form.materials.join(', '));
      setH('volume', form.volume);
      setH('format', form.format);
      setH('budget', form.budget);
      setH('when', form.when);
      const rec = recommend();
      const body = [
        'Demande de devis (assistant en ligne)',
        'Activité: ' + form.activity,
        'Supports: ' + form.materials.join(', '),
        'Volume: ' + form.volume,
        'Format: ' + form.format,
        'Budget: ' + form.budget,
        'Démarrage souhaité: ' + form.when,
        'Recommandation automatique: ' + (rec && rec.name),
        form.note ? 'Précisions: ' + form.note : '',
      ].filter(Boolean).join('\n');
      setH('body', body);
    }

    if (back) back.addEventListener('click', () => { if (step > 1) { step--; render(); } });

    if (contactForm) contactForm.addEventListener('submit', (e) => {
      // Steps 1–4: advance instead of submitting.
      if (!canNext()) { e.preventDefault(); return; }
      if (step < TOTAL) { e.preventDefault(); step++; render(); return; }
      // Final step: write the wizard answers into the hidden fields, then let
      // Shopify receive the POST (emails the store + appears in admin).
      syncHidden();
      const ref = root.querySelector('[data-dossier]');
      if (ref) ref.textContent = 'Demande envoyée · ' + new Date().toLocaleDateString('fr-FR');
    });

    // After a successful Shopify POST the page reloads with a success marker.
    if (root.querySelector('[data-devis-posted]')) {
      step = TOTAL + 1;
    } else if (root.querySelector('[data-devis-errors]')) {
      step = TOTAL;
    }

    render();
  }

  /* ══════════════════════════════════════════════════════════════════
     BOUTIQUE — catalog filter / search / sort
     Pure DOM-driven. Cards carry data-category, data-name, data-search,
     data-price; we toggle [hidden] on the ones that don't match.
     ══════════════════════════════════════════════════════════════════ */

  function initBoutique(scope) {
    const root = (scope || document).querySelector('[data-boutique-catalog]');
    if (!root) return;

    const tabs    = Array.from(root.querySelectorAll('[data-cat-filter]'));
    const search  = root.querySelector('[data-shop-search]');
    const sortSel = root.querySelector('[data-shop-sort]');
    const grid    = root.querySelector('[data-shop-grid]');
    const empty   = root.querySelector('[data-shop-empty]');
    const resetBt = root.querySelector('[data-shop-reset]');
    const cards   = grid ? Array.from(grid.querySelectorAll('[data-product-card]')) : [];

    if (!cards.length) return;

    const state = { cat: 'all', q: '' };
    const norm  = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

    // Hydrate cat tab counts from rendered cards.
    const counts = { all: cards.length };
    cards.forEach((c) => {
      const k = (c.dataset.category || '').toLowerCase().replace(/\s+/g, '-');
      counts[k] = (counts[k] || 0) + 1;
    });
    root.querySelectorAll('[data-cat-count]').forEach((el) => {
      const k = el.dataset.catCount;
      const n = counts[k];
      if (n != null) el.textContent = n;
    });

    function apply() {
      const q = norm(state.q.trim());
      let visible = 0;
      cards.forEach((card) => {
        const catKey = (card.dataset.category || '').toLowerCase().replace(/\s+/g, '-');
        const catMatch = state.cat === 'all' || catKey === state.cat;
        const searchMatch = !q || norm(card.dataset.search || '').includes(q);
        const show = catMatch && searchMatch;
        card.hidden = !show;
        if (show) visible++;
      });
      if (empty) empty.hidden = visible > 0;
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        state.cat = tab.dataset.catFilter;
        tabs.forEach((t) => {
          const on = t === tab;
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-pressed', String(on));
        });
        apply();
      });
    });

    if (search) {
      let timer = null;
      search.addEventListener('input', () => {
        state.q = search.value;
        clearTimeout(timer);
        timer = setTimeout(apply, 120);
      });
    }

    if (resetBt) {
      resetBt.addEventListener('click', () => {
        state.cat = 'all';
        state.q = '';
        if (search) search.value = '';
        tabs.forEach((t) => {
          const on = t.dataset.catFilter === 'all';
          t.classList.toggle('is-on', on);
          t.setAttribute('aria-pressed', String(on));
        });
        apply();
      });
    }

    if (sortSel) {
      sortSel.addEventListener('change', () => {
        // Server sort — preserves pagination and SEO-able URLs.
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSel.value);
        url.searchParams.delete('page');
        window.location.assign(url.toString());
      });
    }

    apply();
  }

  /* ══════════════════════════════════════════════════════════════════
     WISHLIST — purely client-side via localStorage. The product card
     and any wishlist page can rehydrate from the same key.
     ══════════════════════════════════════════════════════════════════ */
  const WISH_KEY = 'pem_wishlist_v1';
  const readWish = () => {
    try { return JSON.parse(localStorage.getItem(WISH_KEY) || '[]'); }
    catch { return []; }
  };
  const writeWish = (ids) => {
    localStorage.setItem(WISH_KEY, JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent('wishlist:updated'));
  };

  function initWishlist() {
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');
    if (!buttons.length) return;
    const wish = new Set(readWish());

    const sync = () => {
      buttons.forEach((btn) => {
        const id = btn.dataset.wishlistToggle;
        btn.setAttribute('aria-pressed', String(wish.has(id)));
      });
    };
    sync();

    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = btn.dataset.wishlistToggle;
        if (wish.has(id)) wish.delete(id);
        else wish.add(id);
        writeWish(Array.from(wish));
        sync();
      });
    });

    window.addEventListener('wishlist:updated', () => {
      const fresh = new Set(readWish());
      wish.clear();
      fresh.forEach((id) => wish.add(id));
      sync();
    });
  }

  /* ══════════════════════════════════════════════════════════════════
     CART — drawer, AJAX add-to-cart, toast
     Talks to Shopify's native /cart/*.js endpoints. No frameworks.
     ══════════════════════════════════════════════════════════════════ */

  const fmtMoney = (cents) => {
    const n = (cents || 0) / 100;
    return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  };

  function setHeaderCount(n) {
    const badge = document.querySelector('[data-cart-count-header]');
    if (!badge) return;
    badge.textContent = n;
    badge.hidden = n <= 0;
    badge.classList.toggle('is-empty', n <= 0);
    if (n > 0) {
      badge.classList.add('is-bump');
      setTimeout(() => badge.classList.remove('is-bump'), 220);
    }
  }

  function initCart() {
    const drawer  = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');
    if (!drawer || !overlay) return;

    const body   = drawer.querySelector('[data-cart-body]');
    const foot   = drawer.querySelector('[data-cart-foot]');
    const count  = drawer.querySelector('[data-cart-count]');
    const plural = drawer.querySelector('[data-cart-count-plural]');
    const sub    = drawer.querySelector('[data-cart-subtotal]');
    const tax    = drawer.querySelector('[data-cart-tax]');
    const tot    = drawer.querySelector('[data-cart-total]');
    const toast  = document.querySelector('[data-cart-toast]');
    const toastMsg = toast && toast.querySelector('[data-cart-toast-msg]');

    let lastFocus = null;

    function openDrawer() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-cart-open');
      // Focus the close button for keyboard users.
      const close = drawer.querySelector('[data-cart-close]');
      if (close) requestAnimationFrame(() => close.focus());
    }
    function closeDrawer() {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-cart-open');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    // Trigger map: any [data-cart-open] opens; [data-cart-close] closes.
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener) { e.preventDefault(); openDrawer(); return; }
      const closer = e.target.closest('[data-cart-close]') || e.target.closest('[data-cart-close-link]');
      if (closer && drawer.contains(closer)) {
        if (closer.tagName === 'A') closeDrawer();
        else { e.preventDefault(); closeDrawer(); }
      }
    });

    overlay.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    /* ── Fetch + render ───────────────────────────────────────── */
    async function fetchCart() {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('Cart fetch failed');
      return res.json();
    }

    function renderItem(item) {
      const img = item.image
        ? `<img class="cart-item__img" loading="lazy" src="${item.image.replace(/(\.[^.]+)$/, '_144x$1')}" alt="${item.product_title.replace(/"/g, '&quot;')}">`
        : `<span class="cart-item__viz-fallback">${(item.product_type || 'RF').slice(0, 4).toUpperCase()}</span>`;
      const opt = item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="cart-item__opt">${item.variant_title}</div>` : '';
      return `
        <div class="cart-item" data-cart-item data-key="${item.key}">
          <a href="${item.url}" class="cart-item__viz" data-cart-close-link>${img}</a>
          <div class="cart-item__body">
            <div class="cart-item__head">
              <div>
                <a href="${item.url}" class="cart-item__title" data-cart-close-link>${item.product_title}</a>
                ${opt}
              </div>
              <button type="button" class="cart-item__rm" data-cart-remove="${item.key}" aria-label="Retirer ${item.product_title.replace(/"/g, '&quot;')}">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
              </button>
            </div>
            <div class="cart-item__foot">
              <div class="cart-item__qty">
                <button type="button" data-cart-qty="-1" data-key="${item.key}" aria-label="Diminuer">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M5 12h14"/></svg>
                </button>
                <span data-cart-qty-val>${item.quantity}</span>
                <button type="button" data-cart-qty="+1" data-key="${item.key}" aria-label="Augmenter">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
                </button>
              </div>
              <div class="cart-item__price">
                <div class="cart-item__price-v">${fmtMoney(item.final_line_price)}</div>
                <div class="cart-item__price-u">HT</div>
              </div>
            </div>
          </div>
        </div>`;
    }

    function renderEmpty() {
      return `
        <div class="cart-drawer__empty">
          <div class="cart-drawer__empty-icon">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8h14l-1 12H6L5 8Z"/><path d="M9 8a3 3 0 0 1 6 0"/></svg>
          </div>
          <div class="cart-drawer__empty-title">Votre panier est vide</div>
          <div class="cart-drawer__empty-text">Découvrez nos imprimantes UV professionnelles, encres et pièces — tout est en stock.</div>
          <div class="cart-drawer__empty-actions">
            <a href="/pages/machine" class="btn btn--ink" data-cart-close-link>Voir les imprimantes</a>
            <a href="/collections/all" class="btn btn--ghost" data-cart-close-link>Boutique</a>
          </div>
        </div>`;
    }

    function renderProgress(subtotalCents) {
      const threshold = +drawer.dataset.threshold || +(document.documentElement.dataset.freeShipThreshold) || 50000;
      if (!subtotalCents) return '';
      const remaining = threshold - subtotalCents;
      if (remaining <= 0) {
        return `<div class="cart-progress"><div class="cart-progress__done">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5 9-11"/></svg>
          Livraison offerte ✓
        </div></div>`;
      }
      const pct = Math.max(0, Math.min(100, (subtotalCents / threshold) * 100));
      return `<div class="cart-progress">
        <div class="cart-progress__row"><span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>
          Plus que ${fmtMoney(remaining)} pour la livraison offerte
        </span></div>
        <div class="cart-progress__bar"><span style="width:${pct}%"></span></div>
      </div>`;
    }

    function render(cart) {
      const n = cart.item_count;
      setHeaderCount(n);
      if (count) count.textContent = n;
      if (plural) plural.textContent = n > 1 ? 's' : '';

      if (n === 0) {
        body.innerHTML = renderEmpty();
        foot.hidden = true;
        return;
      }

      foot.hidden = false;
      const itemsHTML = cart.items.map(renderItem).join('');
      body.innerHTML = itemsHTML;

      if (sub) sub.textContent = fmtMoney(cart.items_subtotal_price);
      const tva = Math.round(cart.items_subtotal_price * 0.20);
      if (tax) tax.textContent = fmtMoney(tva);
      if (tot) tot.textContent = fmtMoney(cart.items_subtotal_price + tva);
    }

    async function refresh({ open } = {}) {
      try {
        const cart = await fetchCart();
        render(cart);
        if (open) openDrawer();
      } catch (err) {
        console.warn('[cart] refresh failed', err);
      }
    }

    /* ── Quantity / remove (event-delegated) ──────────────────── */
    body.addEventListener('click', async (e) => {
      const qtyBtn = e.target.closest('[data-cart-qty]');
      const rmBtn  = e.target.closest('[data-cart-remove]');
      if (!qtyBtn && !rmBtn) return;
      e.preventDefault();

      const updates = {};
      if (qtyBtn) {
        const key = qtyBtn.dataset.key;
        const row = body.querySelector(`[data-key="${CSS.escape(key)}"]`);
        const valEl = row && row.querySelector('[data-cart-qty-val]');
        const cur = +(valEl && valEl.textContent) || 1;
        const delta = qtyBtn.dataset.cartQty === '-1' ? -1 : 1;
        updates[key] = Math.max(0, cur + delta);
      } else if (rmBtn) {
        updates[rmBtn.dataset.cartRemove] = 0;
      }

      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!res.ok) throw new Error('update failed');
        const cart = await res.json();
        render(cart);
      } catch (err) {
        console.warn('[cart] update failed', err);
      }
    });

    /* ── AJAX add-to-cart on product cards ──────────────────── */
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-add-to-cart-form]');
      if (!form) return;
      e.preventDefault();

      const btn  = form.querySelector('[data-add-to-cart]');
      const data = new FormData(form);

      try {
        if (btn) btn.disabled = true;
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: data,
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody.description || 'add failed');
        }
        const added = await res.json();
        await refresh();
        showToast(added.product_title || added.title || 'Produit ajouté');
        if (btn) {
          btn.classList.add('is-added');
          setTimeout(() => btn.classList.remove('is-added'), 1500);
        }
      } catch (err) {
        console.warn('[cart] add failed', err);
        if (btn) {
          btn.textContent = 'Indisponible';
          setTimeout(() => location.reload(), 1400);
        }
      } finally {
        if (btn) btn.disabled = false;
      }
    });

    /* ── Toast ──────────────────────────────────────────────── */
    let toastTimer = null;
    function showToast(msg) {
      if (!toast) return;
      if (toastMsg) toastMsg.textContent = msg + ' ajouté';
      toast.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => { toast.hidden = true; }, 2800);
    }

    /* Initial sync — handles back/forward cache and form-POST returns. */
    refresh();
  }

  /* ── Mobile menu: slide-in off-canvas nav ───────────────────────────── */
  function initMobileMenu() {
    const burger  = document.querySelector('[data-menu-open]');
    const drawer  = document.querySelector('[data-mobile-nav]');
    const overlay = document.querySelector('[data-mobile-nav-overlay]');
    if (!burger || !drawer) return;

    let lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      burger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('is-menu-open');
      const close = drawer.querySelector('[data-menu-close]');
      if (close) requestAnimationFrame(() => close.focus());
    }
    function close() {
      drawer.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      burger.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('is-menu-open');
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    burger.addEventListener('click', open);
    if (overlay) overlay.addEventListener('click', close);
    drawer.querySelectorAll('[data-menu-close]').forEach((el) => el.addEventListener('click', close));
    // Close after tapping any in-page (hash) link so the target is visible.
    drawer.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => {
      const href = a.getAttribute('href') || '';
      if (href.indexOf('#') !== -1) close();
    }));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
    // Auto-close if the viewport grows back to desktop.
    window.addEventListener('resize', () => {
      if (window.innerWidth > 1100 && drawer.classList.contains('is-open')) close();
    }, { passive: true });
  }

  /* ── Header search: send the (previously dead) button to /search ────── */
  function initHeaderSearch() {
    document.querySelectorAll('[data-header-search]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.dataset.headerSearch || '/search';
        window.location.assign(url);
      });
    });
  }

  /* ── Product detail page: gallery, quantity, variant selection ──────── */
  function initProduct(scope) {
    const root = (scope || document).querySelector('[data-product]');
    if (!root) return;

    // Gallery thumbnails → swap the main image.
    const main = root.querySelector('[data-pdp-main]');
    const thumbs = root.querySelectorAll('[data-pdp-thumb]');
    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        if (main && t.dataset.pdpThumb) main.src = t.dataset.pdpThumb;
        thumbs.forEach((x) => x.setAttribute('aria-pressed', String(x === t)));
      });
    });

    // Quantity stepper.
    const qty = root.querySelector('[data-pdp-qty]');
    const minus = root.querySelector('[data-qty-minus]');
    const plus = root.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', () => { if (qty) qty.value = Math.max(1, (+qty.value || 1) - 1); });
    if (plus) plus.addEventListener('click', () => { if (qty) qty.value = (+qty.value || 1) + 1; });

    // Variant selection → update hidden id, price, availability.
    let variants = [];
    const data = root.querySelector('[data-pdp-variants]');
    if (data) { try { variants = JSON.parse(data.textContent); } catch (e) { variants = []; } }
    if (!variants.length) return;

    const idInput = root.querySelector('[data-variant-id]');
    const priceEl = root.querySelector('[data-pdp-price]');
    const ttcEl = root.querySelector('[data-pdp-ttc]');
    const addBtn = root.querySelector('[data-add-to-cart]');
    const addLabel = root.querySelector('[data-add-label]');
    const fr2 = (cents) => (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    function update() {
      const sel = Array.from(root.querySelectorAll('[data-pdp-option]:checked')).map((i) => i.value);
      const v = variants.find((vt) => sel.every((val, i) => vt.options[i] === val)) || variants[0];
      if (!v) return;
      if (idInput) idInput.value = v.id;
      if (priceEl && v.price != null) priceEl.textContent = fr2(v.price);
      if (ttcEl && v.price != null) ttcEl.textContent = fr2(Math.round(v.price * 1.2));
      if (addBtn) addBtn.disabled = !v.available;
      if (addLabel) addLabel.textContent = v.available ? (addLabel.dataset.instock || 'Ajouter au panier') : (addLabel.dataset.oos || 'Épuisé');
    }
    root.querySelectorAll('[data-pdp-option]').forEach((i) => i.addEventListener('change', update));
    update();
  }

  /* ══════════════════════════════════════════════════════════════════
     Override boot to include the new boutique + cart initializers.
     ══════════════════════════════════════════════════════════════════ */
  function boot() {
    initProduct();
    initReveal();
    initHeaderShadow();
    initMobileMenu();
    initHeaderSearch();
    initFAQ();
    initGalleryFilters();
    initMachineTabs();
    initROI();
    initDevis();
    initBoutique();
    initWishlist();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ── Theme editor: re-init a section when it is added/edited ──────────
     Shopify replaces a section's HTML on edit; without this its `.reveal`
     elements stay hidden (section looks empty) and its interactivity dies. */
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target;
    revealAll(root);
    initFAQ(root);
    initGalleryFilters(root);
    initMachineTabs(root);
    initROI(root);
    initDevis(root);
    initBoutique(root);
    initProduct(root);
  });
  document.addEventListener('shopify:section:select', (e) => revealAll(e.target));
})();
